// src/pages/admin/AdminTournamentDashboard.js
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useLocale } from "../../contexts/LocaleContext";
import { useNotification } from "../../contexts/NotificationContext";
import tournamentService from "../../services/tournamentService";
import { sortItems } from "../../utils/viewHelpers";

import { HeadingMedium } from "baseui/typography";
import { TableBuilder, TableBuilderColumn } from "baseui/table-semantic";
import { Spinner } from "baseui/spinner";
import { Block } from "baseui/block";
import { Button } from "baseui/button";
import { Tag, KIND } from "baseui/tag";
import { Pagination } from "baseui/pagination";

const STATUS_TO_KIND_MAP = {
  "status.ready": KIND.primary,
  "status.countdown": KIND.accent,
  "status.in_progress": KIND.positive,
  "status.paused": KIND.warning,
  "status.finished": KIND.neutral,
  "status.unknown": KIND.black,
};

function determineStatusKey(timer, structure) {
  switch (timer?.state) {
    case "ready":
      return "status.ready";
    case "play": {
      const currentLevel = structure?.levels?.[timer.rowIndex];
      if (
        timer.rowIndex === 0 &&
        timer.hasTournamentStarted === false &&
        currentLevel?.type === "break"
      ) {
        return "status.countdown";
      }
      return "status.in_progress";
    }
    case "pause":
      return "status.paused";
    case "stop":
      return "status.finished";
    default:
      return "status.unknown";
  }
}

const AdminTournamentDashboard = () => {
  const [tournaments, setTournaments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalTournaments, setTotalTournaments] = useState(0);
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");

  const { currentUser } = useAuth();
  const { t } = useLocale();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const fetchTournaments = useCallback(async () => {
    setIsLoading(true);
    const { data, error } = await tournamentService.getAllTournaments();
    if (error) {
      showNotification(error.message || t("errors.admin.fetchTournaments"), "error");
    } else if (data?.tournaments) {
      setTournaments(
        data.tournaments.map((item) => ({
          ...item,
          name: item.name || "",
          ownerId: item.owner_id,
          createdAt: item.created_at,
          updatedAt: item.updated_at,
          statusKey: determineStatusKey(item.timer_state, item.structure),
        }))
      );
      setTotalTournaments(data.totalCount ?? data.tournaments.length);
    }
    setIsLoading(false);
  }, [showNotification, t]);

  const handleSort = (newSortColumn) => {
    if (sortColumn === newSortColumn) {
      setSortOrder((prev) => (prev === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortColumn(newSortColumn);
      setSortOrder("ASC");
    }
    setPage(1);
  };

  const sortedTournaments = useMemo(
    () => sortItems(tournaments, sortColumn, sortOrder),
    [tournaments, sortColumn, sortOrder]
  );

  const paginatedTournaments = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return sortedTournaments.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedTournaments, page, rowsPerPage]);

  const handleDeleteTournament = async (tournament) => {
    const tournamentName = tournament.name || tournament.id;
    if (window.confirm(t("admin.tournaments.confirmDelete", { name: tournamentName }))) {
      const { data, error: deleteError } = await tournamentService.adminDeleteTournament(
        tournament.id
      );

      if (data?.success) {
        showNotification(t("admin.tournaments.notification.deleteSuccess"), "success");
        fetchTournaments();
      } else {
        showNotification(
          deleteError?.message || t("admin.tournaments.notification.deleteError"),
          "error"
        );
      }
    }
  };

  useEffect(() => {
    if (currentUser?.role === "admin") {
      fetchTournaments();
    } else {
      showNotification(t("errors.admin.notAuthorized"), "error");
      setIsLoading(false);
    }
  }, [currentUser, fetchTournaments, showNotification, t]);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Block>
      <HeadingMedium>{t("admin.tournaments.title")}</HeadingMedium>
      <TableBuilder
        data={paginatedTournaments}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        onSort={handleSort}
      >
        <TableBuilderColumn
          id="id"
          header={t("admin.tournaments.header.tournamentId")}
          sortable
        >
          {(row) => row.id}
        </TableBuilderColumn>
        <TableBuilderColumn id="name" header={t("admin.tournaments.header.name")} sortable>
          {(row) => row.name || t("tournament.untitledTournament", { id: row.id.substring(0, 6) })}
        </TableBuilderColumn>
        <TableBuilderColumn
          id="ownerId"
          header={t("admin.tournaments.header.ownerId")}
          sortable
        >
          {(row) => row.ownerId}
        </TableBuilderColumn>
        <TableBuilderColumn
          id="statusKey"
          header={t("admin.tournaments.header.status")}
          sortable
        >
          {(row) => (
            <Tag closeable={false} kind={STATUS_TO_KIND_MAP[row.statusKey] || KIND.neutral}>
              {t(row.statusKey)}
            </Tag>
          )}
        </TableBuilderColumn>
        <TableBuilderColumn
          id="createdAt"
          header={t("admin.tournaments.header.createdAt")}
          sortable
        >
          {(row) => (row.createdAt ? new Date(row.createdAt).toLocaleString() : "-")}
        </TableBuilderColumn>
        <TableBuilderColumn
          id="updatedAt"
          header={t("admin.tournaments.header.lastUpdated")}
          sortable
        >
          {(row) => new Date(row.updatedAt).toLocaleString()}
        </TableBuilderColumn>
        <TableBuilderColumn header={t("admin.tournaments.header.actions")}>
          {(row) => (
            <>
              <Button size="compact" onClick={() => navigate(`/controller/${row.id}`)}>
                {t("common.openButton")}
              </Button>
              <Button
                size="compact"
                kind="minimal"
                onClick={() => handleDeleteTournament(row)}
                overrides={{
                  BaseButton: {
                    style: ({ $theme }) => ({
                      color: $theme.colors.negative,
                      marginLeft: "8px",
                    }),
                  },
                }}
              >
                {t("admin.tournaments.actions.delete")}
              </Button>
            </>
          )}
        </TableBuilderColumn>
      </TableBuilder>

      <Pagination
        currentPage={page}
        numPages={Math.max(1, Math.ceil(totalTournaments / rowsPerPage))}
        onPageChange={({ nextPage }) => setPage(nextPage)}
        onPrevClick={() => setPage(page - 1)}
        onNextClick={() => setPage(page + 1)}
        onPageSizeChange={({ size }) => setRowsPerPage(size)}
        pageSize={rowsPerPage}
        labels={{
          prevButton: t("common.prev"),
          nextButton: t("common.next"),
        }}
      />
    </Block>
  );
};

export default AdminTournamentDashboard;